import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { RotateCcw, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const isImage = (v) => v.startsWith("data:image") || /^https?:\/\/.+\.(png|jpe?g|webp|gif)/i.test(v) || v.startsWith("https://images.unsplash");

export default function SiteContentPanel() {
  const { save } = useContent() || {};
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/content"); setItems(Object.entries(data).filter(([k]) => !k.startsWith("access."))); } catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = async (key) => {
    if (await save(key, null)) setItems((l) => l.filter(([k]) => k !== key));
  };

  const resetAll = async () => {
    if (!window.confirm("Reset ALL content edits back to the original site defaults?")) return;
    setBusy(true);
    for (const [k] of items) await api.put("/content", { key: k, value: null }).catch(() => {});
    setBusy(false);
    toast.success("All content restored to defaults");
    window.location.reload();
  };

  if (items === null) return <div className="text-zinc-500 text-sm">Loading content edits…</div>;
  if (items.length === 0)
    return (
      <div className="text-sm text-zinc-500" data-testid="site-content-empty">
        No custom edits yet. Use the <span className="text-pb-cyan font-bold">Edit Mode</span> pencil (bottom-left of any page) to change text and photos — your changes will show up here.
      </div>
    );

  return (
    <div data-testid="site-content-panel">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-zinc-500">{items.length} custom edit{items.length === 1 ? "" : "s"} live on the site</span>
        <button data-testid="site-content-reset-all" disabled={busy} onClick={resetAll}
          className="flex items-center gap-1.5 border border-pb-red/60 text-pb-red text-[10px] font-black uppercase tracking-widest px-3 py-2 hover:bg-pb-red hover:text-black transition-all disabled:opacity-50">
          <RotateCcw className="w-3 h-3" /> {busy ? "Resetting…" : "Reset All to Defaults"}
        </button>
      </div>
      <div className="space-y-2">
        {items.sort(([a], [b]) => a.localeCompare(b)).map(([key, value]) => {
          let display = value, orig = null;
          if (key.startsWith("txt.")) {
            try { const p = JSON.parse(value); orig = p.o; display = p.v; } catch {}
          }
          return (
          <div key={key} className="flex items-center gap-3 border border-pb-border bg-pb-elevated/40 px-4 py-3" data-testid={`content-row-${key}`}>
            {isImage(display) ? (
              <img src={display} alt="" className="w-16 h-10 object-cover border border-pb-border shrink-0" />
            ) : (
              <FileText className="w-4 h-4 text-pb-cyan shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                {isImage(display) && <ImageIcon className="w-3 h-3" />}{orig ? `Text edit — was: "${orig.slice(0, 60)}${orig.length > 60 ? "…" : ""}"` : key}
              </div>
              <div className="text-sm text-zinc-300 truncate">{isImage(display) ? "Custom image" : display}</div>
            </div>
            <button data-testid={`content-reset-${key}`} onClick={() => reset(key)} title="Reset this to default"
              className="flex items-center gap-1 text-zinc-500 hover:text-pb-red text-[10px] font-bold uppercase tracking-widest shrink-0 transition-colors">
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          </div>
          );
        })}
      </div>
    </div>
  );
}
